import { useState } from 'react';
import { backdropClose } from '../utils/backdropClose';
// import { supabase } from '../utils/supabase';
import toast from 'react-hot-toast';
import { resolveBilledFrom, type SellerRegistration } from '../utils/invoiceAddress';

// Helper function to convert number to words (Indian numbering system)
function numberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
  if (num < 1000) {
    return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
  }
  if (num < 100000) {
    return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
  }
  if (num < 10000000) {
    return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + numberToWords(num % 100000) : '');
  }
  return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + numberToWords(num % 10000000) : '');
}

interface MembershipPlan {
  id: string;
  name: string;
  description: string | null;
  durationMonths: number | null;
  price: number | null;
  isActive: boolean;
  isDefault: boolean;
}

interface MembershipPaymentRecord {
  id: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  discountAmount: number;
  discountPercent: number;
  taxAmount: number;
  taxPercent: number;
  notes: string | null;
  createdAt: string;
}

interface Purchase {
  id: string;
  memberId: string;
  gymId: string;
  planId: string;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  purchaseDate: string;
  discountAmount: number | null;
  discountPercent: number | null;
  baseAmount: number | null;
  taxAmount: number | null;
  taxPercent: number | null;
  totalAmount: number | null;
  notes: string | null;
  paymentNotes: string | null;
  invoiceId: string | null;
  invoiceGstin?: string | null;
  documentType?: string;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  paymentMethod: string | null;
  paymentStatus: string;
  paidAmount: number | null;
  pendingAmount: number | null;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  plan: MembershipPlan;
  member: {
    id: string;
    fullName: string | null;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
    phoneNumber: string | null;
    address: string | null;
    city: string | null;
    pincode: string | null;
  };
  gym: {
    id: string;
    name: string;
    displayName: string;
    address: string | null;
    registeredAddress: string | null;
    gstin: string | null;
    includeGstinInInvoices?: boolean;
    phoneNumbers: string[];
    email: string;
    gymCode: string | null;
    /** To name the registration behind the invoice's GSTIN (legal name, registered address). */
    gstRegistrations?: SellerRegistration[];
  };
  creator: {
    id: string;
    name: string | null;
    displayName: string | null;
  } | null;
  membershipPayments?: MembershipPaymentRecord[];
}

interface ViewPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchase: Purchase | null;
}

export default function ViewPurchaseModal({ isOpen, onClose, purchase }: ViewPurchaseModalProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!purchase) return;

    setDownloading(true);
    try {
      const invoiceElement = document.getElementById('invoice-content');
      if (!invoiceElement) {
        toast.error('Invoice content not found');
        return;
      }

      const html2pdf = (await import('html2pdf.js')).default;

      const opt = {
        margin: 0.5,
        filename: `invoice-${purchase.invoiceId || purchase.id}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait'
        } as const
      };

      await html2pdf().set(opt).from(invoiceElement).save();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen || !purchase) return null;

  const rawPaymentStatus = purchase.paymentStatus || 'paid';
  const isCancelled = purchase.status === 'cancelled';
  const isRefund = purchase.status === 'refund';
  const isBillOfSupply = purchase.documentType === 'bill_of_supply';
  const sellerName = purchase.gym.displayName || purchase.gym.name;
  const billedFrom = resolveBilledFrom(purchase as any, sellerName);
  const isOverdue = !isCancelled && !isRefund && (rawPaymentStatus === 'due' || rawPaymentStatus === 'partial') && !!purchase.dueDate && new Date(purchase.dueDate) < new Date();
  const paymentStatus = isOverdue ? 'overdue' : rawPaymentStatus;
  const paidAmount = Number(purchase.paidAmount || purchase.totalAmount || 0);
  const pendingAmount = Number(purchase.pendingAmount || 0);
  const payments = purchase.membershipPayments || [];

  // Amount Calculations (Falling back to manual calculation if backend fields are missing)
  const basePrice = Number(purchase.baseAmount || purchase.plan.price || 0);
  const discountPercent = Number(purchase.discountPercent || 0);

  // Use discountAmount if available, otherwise calculate from percent
  const totalDiscount = purchase.discountAmount !== null && purchase.discountAmount !== undefined
    ? Number(purchase.discountAmount)
    : (basePrice * discountPercent) / 100;

  const subtotal = Math.max(0, basePrice - totalDiscount);

  const taxPercent = Number(purchase.taxPercent || 18);
  const taxAmount = Number(purchase.taxAmount || (subtotal * taxPercent) / 100);
  const totalAmount = Number(purchase.totalAmount || (subtotal + taxAmount));

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-0 sm:p-6 animate-in fade-in duration-300" {...backdropClose(onClose)}>
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl w-[96%] sm:w-11/12 max-w-5xl max-h-[92dvh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-top-8 duration-500 mb-0 sm:mb-0" onClick={(e) => e.stopPropagation()}>
        {/* Header - Premium Dynamic Gradient */}
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 p-4 sm:p-8 text-white relative shadow-xl shrink-0">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-inner shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-black tracking-tight leading-none truncate">{isBillOfSupply ? 'Bill of Supply' : 'Tax Invoice'}</h2>
                <div className="flex items-center gap-1.5 sm:gap-2 mt-1 sm:mt-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 bg-white/20 rounded-md text-[8px] sm:text-[10px] font-black uppercase tracking-widest border border-white/10 backdrop-blur-sm shrink-0">
                    {purchase.status === 'refund' ? 'REFUNDED' : (purchase.status || 'Active')}
                  </span>
                  {paymentStatus === 'overdue' && (
                    <span className="px-1.5 py-0.5 bg-red-600/90 rounded-md text-[8px] sm:text-[10px] font-black uppercase tracking-widest border border-red-400/30 shrink-0 animate-pulse">OVERDUE</span>
                  )}
                  {paymentStatus === 'due' && !isCancelled && (
                    <span className="px-1.5 py-0.5 bg-red-500/80 rounded-md text-[8px] sm:text-[10px] font-black uppercase tracking-widest border border-red-400/30 shrink-0">DUE</span>
                  )}
                  {paymentStatus === 'partial' && !isCancelled && (
                    <span className="px-1.5 py-0.5 bg-amber-500/80 rounded-md text-[8px] sm:text-[10px] font-black uppercase tracking-widest border border-amber-400/30 shrink-0">PARTIAL</span>
                  )}
                  <span className="text-[10px] sm:text-xs font-bold text-blue-100 opacity-80 truncate">#{purchase.invoiceId || purchase.id.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all border border-white/20 backdrop-blur-md active:scale-95 disabled:opacity-50 font-black text-xs uppercase tracking-widest"
              >
                {downloading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <span>{downloading ? 'Syncing...' : 'Export PDF'}</span>
              </button>

              <button
                onClick={onClose}
                className="p-2 sm:p-3 bg-white/10 hover:bg-red-500 rounded-xl sm:rounded-2xl transition-all border border-white/20 backdrop-blur-sm active:scale-90 group"
                aria-label="Close"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 sm:p-8 bg-gray-100">
          <div id="invoice-content" className="bg-white p-4 sm:p-10 shadow-sm mx-auto w-full sm:max-w-[210mm] text-xs sm:text-sm relative overflow-hidden" style={{ fontFamily: '"Inter", sans-serif', minHeight: 'auto' }}>
            {/* Watermark for Refunds */}
            {purchase.status === 'refund' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 select-none overflow-hidden">
                <div className="text-red-500/10 text-[120px] font-black uppercase tracking-[0.2em] -rotate-45 whitespace-nowrap border-8 border-red-500/10 px-12 py-4 rounded-[40px]">
                  Refunded
                </div>
              </div>
            )}
            {/* Watermark for Due / Partial / Overdue */}
            {(paymentStatus === 'due' || paymentStatus === 'partial' || paymentStatus === 'overdue') && !isCancelled && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 select-none overflow-hidden">
                <div className={`text-[80px] font-black uppercase tracking-[0.2em] -rotate-45 whitespace-nowrap border-8 px-12 py-4 rounded-[40px] ${paymentStatus === 'overdue' ? 'text-red-600/10 border-red-600/10' : paymentStatus === 'due' ? 'text-red-500/10 border-red-500/10' : 'text-amber-500/10 border-amber-500/10'}`}>
                  {paymentStatus === 'overdue' ? 'Overdue' : paymentStatus === 'due' ? 'Payment Due' : 'Partial'}
                </div>
              </div>
            )}

            {/* Header: Title & Meta */}
            <div className="flex flex-col sm:flex-row justify-between items-start mb-6 pb-4 border-b border-gray-100 gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight uppercase leading-none">{isBillOfSupply ? 'Bill of Supply' : 'Tax Invoice'}</h1>
                <p className="text-gray-500 font-mono text-xs mt-1">
                  Invoice No: <span className="font-bold text-gray-900">{purchase.invoiceId || purchase.id.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-0.5">Invoice Date</p>
                <p className="text-sm font-bold text-gray-900">
                  {new Date(purchase.purchaseDate).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {/* Combined Grid: From, To, Payment */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6">
              {/* Col 1: Gym Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Billed From</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{sellerName}</p>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5 leading-snug">
                    {billedFrom.legalName && <p className="text-gray-800">{billedFrom.legalName}</p>}
                    {billedFrom.address && <p>{billedFrom.address}</p>}
                    {billedFrom.branchAddress && <p>Branch: {billedFrom.branchAddress}</p>}
                    {!isBillOfSupply && purchase.invoiceGstin && (
                      <p className="font-bold text-gray-900">GSTIN: {purchase.invoiceGstin}</p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-600 space-y-0.5 pt-1">
                  <p className="break-all"><span className="text-gray-500 font-bold w-10 inline-block">Email:</span> {purchase.gym.email}</p>
                  {purchase.gym.phoneNumbers?.length > 0 && (
                    <p><span className="text-gray-500 font-bold w-10 inline-block">Phone:</span> {purchase.gym.phoneNumbers[0]}</p>
                  )}
                </div>
              </div>

              {/* Col 2: Consumer & Payment */}
              <div className="space-y-4">
                {/* Bill To */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Billed To</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{purchase.member.displayName || purchase.member.fullName || 'N/A'}</p>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5 leading-snug">
                    {purchase.member.address && <p>{purchase.member.address}</p>}
                    {purchase.member.city && (
                      <p>{purchase.member.city}{purchase.member.pincode ? ` - ${purchase.member.pincode}` : ''}</p>
                    )}
                    {(purchase.member.phoneNo || purchase.member.phoneNumber) && (
                      <p><span className="text-gray-500 font-bold">Mob:</span> {purchase.member.phoneNo || purchase.member.phoneNumber}</p>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Payment Method</p>
                  <span className="inline-block text-xs font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded uppercase">
                    {purchase.paymentMethod || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="mb-6 overflow-x-auto">
              <table className="w-full min-w-full">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Description</th>
                    <th className="py-2 px-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-center w-12 sm:w-16">Qty</th>
                    <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-right w-20 sm:w-24">Unit Price</th>
                    <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-right w-20 sm:w-28">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 pr-2 sm:pr-4 align-top">
                      <p className="text-sm font-bold text-gray-900">{purchase.plan.name}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5 font-bold tracking-wider uppercase">
                        {purchase.plan.durationMonths || 1} {purchase.plan.durationMonths === 1 ? 'Month' : 'Months'} Access Plan
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5 italic font-medium">
                        {purchase.startDate ? new Date(purchase.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                        {' - '}
                        {purchase.endDate ? new Date(purchase.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                      </p>
                    </td>
                    <td className="py-3 px-2 text-center font-bold text-gray-900 text-sm align-top">1.00</td>
                    <td className="py-3 text-right font-medium text-gray-900 text-sm align-top">
                      ₹{basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 text-right font-bold text-gray-900 text-sm align-top">
                      ₹{basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer / Totals Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start border-t border-gray-200 pt-6 gap-6 sm:gap-0">
              {/* Left: Notes & Terms */}
              <div className="w-full sm:w-1/2 pr-0 sm:pr-8 space-y-6 order-2 sm:order-1">
                {/* Amount in Words */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Amount in Words</p>
                  <p className="text-xs font-bold text-gray-600 leading-tight capitalize">
                    {numberToWords(Math.round(totalAmount))} Rupees Only
                  </p>
                </div>

                {(purchase.notes || purchase.paymentNotes) && (
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Remarks</p>
                    <p className="text-[10px] text-gray-600 italic leading-relaxed">
                      {purchase.paymentNotes ? `"${purchase.paymentNotes}"` : `"${purchase.notes}"`}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Terms & Conditions</p>
                  <ul className="text-[9px] text-gray-500 list-disc list-inside space-y-0.5 leading-snug">
                    <li>Membership fees are non-refundable & non-transferable.</li>
                    <li>Valid only for the period specified above.</li>
                    <li>Please maintain gym decorum & safety protocols.</li>
                  </ul>
                </div>
              </div>

              {/* Right: Totals */}
              <div className="w-full sm:w-1/2 max-w-xs space-y-2 ml-auto order-1 sm:order-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">Base Amount</span>
                  <span className="text-gray-900 font-bold">₹{basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>

                {totalDiscount > 0 && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-red-500 font-bold uppercase text-[10px] tracking-widest">
                      Discount {discountPercent > 0 ? `(${discountPercent}%)` : ''}
                    </span>
                    <span className="text-red-600 font-bold">-₹{totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {!isBillOfSupply && taxAmount > 0 && (
                  Number(purchase.igstAmount) > 0 ? (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">IGST</span>
                      <span className="text-gray-900 font-bold">₹{Number(purchase.igstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ) : (Number(purchase.cgstAmount) > 0 || Number(purchase.sgstAmount) > 0) ? (
                    <>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">CGST</span>
                        <span className="text-gray-900 font-bold">₹{Number(purchase.cgstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">SGST</span>
                        <span className="text-gray-900 font-bold">₹{Number(purchase.sgstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">GST ({taxPercent}%)</span>
                      <span className="text-gray-900 font-bold">₹{taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )
                )}

                <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-gray-900">
                  <span className="text-lg font-black text-gray-900 uppercase tracking-tight">Grand Total</span>
                  <span className="text-xl sm:text-2xl font-black text-blue-600 tracking-tight">
                    ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <p className="text-right text-[9px] text-gray-400 font-bold uppercase tracking-widest">Inclusive of all applicable taxes</p>
              </div>
            </div>

            {/* Payment Summary Section — shown for partial/due/overdue invoices */}
            {(paymentStatus === 'partial' || paymentStatus === 'due' || paymentStatus === 'overdue') && !isCancelled && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Payment Summary</p>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Total</p>
                    <p className="text-sm font-black text-gray-900 mt-0.5">₹{totalAmount.toFixed(2)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-[9px] font-black text-green-600 uppercase tracking-widest">Paid</p>
                    <p className="text-sm font-black text-green-700 mt-0.5">₹{paidAmount.toFixed(2)}</p>
                  </div>
                  <div className={`rounded-xl p-3 text-center ${paymentStatus === 'overdue' ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <p className={`text-[9px] font-black uppercase tracking-widest ${paymentStatus === 'overdue' ? 'text-red-600' : 'text-amber-600'}`}>
                      {paymentStatus === 'overdue' ? 'Overdue' : 'Pending'}
                    </p>
                    <p className={`text-sm font-black mt-0.5 ${paymentStatus === 'overdue' ? 'text-red-700' : 'text-amber-700'}`}>₹{pendingAmount.toFixed(2)}</p>
                  </div>
                </div>
                {purchase.dueDate && (
                  <p className={`text-[9px] font-bold mb-3 ${paymentStatus === 'overdue' ? 'text-red-600' : 'text-amber-600'}`}>
                    {paymentStatus === 'overdue' ? '⚠ Due date passed: ' : 'Due by: '}
                    {new Date(purchase.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            )}

            {/* Payment History Table — shown whenever there are recorded payment receipts */}
            {payments.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100" style={{ pageBreakBefore: payments.length > 3 ? 'always' : 'auto' }}>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Payment History</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-gray-900">
                      <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left w-8">#</th>
                      <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-right">Amount</th>
                      <th className="py-2 px-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Date Paid</th>
                      <th className="py-2 px-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Method</th>
                      <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-right">GST</th>
                      <th className="py-2 pl-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map((payment, idx) => (
                      <tr key={payment.id}>
                        <td className="py-2.5 text-[10px] font-black text-gray-400">{idx + 1}</td>
                        <td className="py-2.5 text-right font-bold text-gray-900">₹{Number(payment.amount).toFixed(2)}</td>
                        <td className="py-2.5 px-2 text-gray-700 font-medium">
                          {new Date(payment.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="py-2.5 px-2 text-gray-700 font-medium uppercase text-[10px]">{payment.paymentMethod}</td>
                        <td className="py-2.5 text-right text-gray-500">
                          {Number(payment.taxAmount) > 0 ? `₹${Number(payment.taxAmount).toFixed(2)}` : '—'}
                        </td>
                        <td className="py-2.5 pl-2 text-gray-400 italic text-[10px] max-w-[100px] truncate">{payment.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-900">
                      <td className="pt-2 text-[10px] font-black uppercase text-gray-400">Total</td>
                      <td className="pt-2 text-right font-black text-gray-900">
                        ₹{payments.reduce((sum, p) => sum + Number(p.amount), 0).toFixed(2)}
                      </td>
                      <td colSpan={4}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Absolute Bottom Footer */}
            <div className="mt-8 sm:mt-12 pt-6 border-t border-gray-50 text-center">
              <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest italic">Computer Generated Document • No signature required</p>
              <p className="text-[8px] text-gray-300 font-bold uppercase tracking-[0.2em] mt-1">
                Generated on {new Date().toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Fixed Export Button - Premium Sticky Footer */}
        <div className="md:hidden p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] focus-within:ring-0">
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-blue-100 flex items-center justify-center gap-3 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {downloading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            <span>{downloading ? 'Preparing...' : 'Export Invoice PDF'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const X = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
